import React, { useState } from 'react';
import { Card, Form, Input, InputNumber, Button, Alert, Typography, Space } from 'antd';
import { CarOutlined, LockOutlined } from '@ant-design/icons';
import { publicApi } from '../../services/apiInstances';
import { setChallengeToken } from '../../services/portalChallenge';

const { Title, Paragraph, Text } = Typography;

/**
 * WP-04 — "we use the car as a lock".
 *
 * Shown when the backend answers `PORTAL_ACCESS_REQUIRED` for a document. The
 * customer proves they know the vehicle; the server mints a session scoped to
 * this one document and nothing else.
 *
 * ── What this component must NOT do ───────────────────────────────────────
 * It must not tell the customer anything the server refused to. The server
 * answers one constant 401 whether the link is real, revoked, or belongs to
 * somebody else — so this form renders identically in all of those cases, and
 * a wrong answer says only "those details do not match". Rendering a different
 * message for a link that "does not exist" would put back, in the browser, the
 * enumeration oracle the backend went to some trouble to close.
 *
 * Nothing here is a security control: the gate is entirely server-side. This is
 * the door, not the lock.
 */

export interface VehicleChallengeGateProps {
  /** The document's public token, from the URL. */
  documentToken: string;
  /** 'estimates' | 'work-orders' — which public route family to challenge. */
  resource: 'estimates' | 'work-orders';
  /** Called after a session is stored, so the page can retry its fetch. */
  onPassed: () => void;
  /** Optional: where a customer with an account should go to sign in. */
  loginPath?: string;
}

const VehicleChallengeGate: React.FC<VehicleChallengeGateProps> = ({
  documentToken,
  resource,
  onPassed,
  loginPath = '/portal/login',
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [retryAfter, setRetryAfter] = useState<number | null>(null);

  const submit = async (values: { year: number; make: string; model: string }) => {
    setSubmitting(true);
    setMessage(null);
    setRetryAfter(null);
    try {
      const res = await publicApi.post(`/public/${resource}/${documentToken}/challenge`, {
        year: values.year,
        make: values.make,
        model: values.model,
      });
      const token = res.data?.challenge_token;
      if (!token) {
        setMessage('Something went wrong. Please try again.');
        return;
      }
      setChallengeToken(documentToken, token);
      onPassed();
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 429) {
        const secs = err?.response?.data?.retryAfterSeconds ?? null;
        setRetryAfter(secs);
        setMessage(
          secs
            ? `Too many attempts. Please try again in about ${Math.ceil(secs / 60)} minute(s), or contact the shop.`
            : 'Too many attempts. Please try again later, or contact the shop.'
        );
      } else {
        // One message for every failure, matching the server. Do not elaborate.
        setMessage('Those vehicle details do not match this document.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ maxWidth: 480, margin: '48px auto', padding: '0 16px' }}>
      <Card>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div style={{ textAlign: 'center' }}>
            <LockOutlined style={{ fontSize: 32 }} />
            <Title level={4} style={{ marginTop: 12, marginBottom: 4 }}>
              Verify your vehicle
            </Title>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              To protect your information, tell us which vehicle this document is for.
            </Paragraph>
          </div>

          {message && (
            <Alert
              type={retryAfter ? 'warning' : 'error'}
              message={message}
              showIcon
              data-testid="challenge-error"
            />
          )}

          <Form form={form} layout="vertical" onFinish={submit} disabled={submitting}>
            <Form.Item
              label="Year"
              name="year"
              rules={[{ required: true, message: 'Enter the vehicle year' }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="2019"
                controls={false}
                inputMode="numeric"
              />
            </Form.Item>
            <Form.Item
              label="Make"
              name="make"
              rules={[{ required: true, message: 'Enter the vehicle make' }]}
            >
              <Input placeholder="Subaru" autoComplete="off" />
            </Form.Item>
            <Form.Item
              label="Model"
              name="model"
              rules={[{ required: true, message: 'Enter the vehicle model' }]}
            >
              <Input placeholder="Outback" autoComplete="off" />
            </Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              block
              size="large"
              loading={submitting}
              icon={<CarOutlined />}
            >
              View document
            </Button>
          </Form>

          <div style={{ textAlign: 'center' }}>
            <Text type="secondary">
              Already have an account? <a href={loginPath}>Sign in</a> instead.
            </Text>
          </div>
        </Space>
      </Card>
    </div>
  );
};

export default VehicleChallengeGate;
